import { useEffect } from 'react'

// Constants
import { useFlag } from 'constants/flags'
import localStorageKeys from 'constants/localStorageKeys'

// Utils
import { getParsedItem } from 'utils/localStorage'

// Hooks
import { useModalContext } from 'containers/ModalManager/ModalContext'

// Components
import WorkspaceWelcomeModal from 'modules/workspace/modals/WorkspaceWelcomeModal'

// Types
import { WorkspaceMinimalFragment, useGetPaymentDetailsQuery } from 'gqlTypes'

/**
 * Renders a Welcome Modal for Workspaces
 * Allows the user to subscribe right away or keep
 * using the Workspace as a Trial.
 *
 * Will only render if:
 *  - User is Owner of the Workspace
 *  - Was not dismissed before
 *  - Workspace is on trial
 *  - Workspace is not subscribed
 */
const useWelcomeModal = (workspace: WorkspaceMinimalFragment) => {
  const { showModal } = useModalContext()

  // TODO: Remove feature flag when released to production
  // Issue: https://github.com/sketch-hq/Cloud/issues/10012
  const isOnBoardingLicensesSignUpEnabled = useFlag(
    'onboarding-licenses-signup'
  )

  const { data } = useGetPaymentDetailsQuery({
    variables: { customerId: workspace.customer?.identifier! },
    skip: !workspace.customer?.identifier,
  })

  const isSubscribed = data?.customer.paymentDetails.type !== 'NONE'
  const isTrial = workspace.customer?.billing?.status === 'TRIALING'

  const wasWelcomeModalDismissed = getParsedItem(
    localStorageKeys.dismissedWelcomeModal
  )?.includes(workspace.identifier)

  const shouldRenderWelcomeModal =
    isOnBoardingLicensesSignUpEnabled &&
    workspace.userIsOwner &&
    !wasWelcomeModalDismissed &&
    isTrial &&
    !isSubscribed

  /** Only render the modal according to the conditions above*/
  useEffect(() => {
    if (shouldRenderWelcomeModal) {
      showModal(WorkspaceWelcomeModal, { workspaceId: workspace.identifier })
    }
  }, [showModal, shouldRenderWelcomeModal, workspace])
}

export default useWelcomeModal
